// Import the source module (not the barrel) to match `account-api.ts` — the
// barrel's init graph would create an import cycle for pre-auth modules.
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { getAnalyticsMetaProperties } from '@/services/analytics/meta-properties';
import { getOrCreateSignupAnonymousId } from '@/services/signup/anonymous-id';

/**
 * Pre-auth URLs can carry credentials or PII in their query string — the login
 * page's 2FA email-link `token`, and the funnels' `email`/`first`/`last`
 * prefills — and step navigation preserves query params, so the raw
 * `url`/`search` meta properties would leak them into analytics. Strip them
 * before reporting. (Ember sent the full href; this closes that off.)
 */
const SENSITIVE_QUERY_PARAMS = ['token', 'email', 'first', 'last'];

function sanitizedLocation(): { url: string; search: string } {
  const url = new URL(window.location.href);
  for (const param of SENSITIVE_QUERY_PARAMS) {
    url.searchParams.delete(param);
  }
  return { url: url.toString(), search: url.search };
}

/**
 * Fire a pre-auth analytics event for anonymous visitors (login, signup).
 *
 * Pre-auth pages run before any workspace exists, so they can't use Hydra's
 * workspace-scoped `useAnalytics` (which posts to the environment-scoped
 * tracking endpoint). Instead this mirrors what Ember's anonymous tracking
 * did: post to the global `track_anon` endpoint with the same event names, the
 * standard `getAnalyticsMetaProperties()` block every Hydra/Ember event
 * carries, the per-event properties, and the `anon_id` (so anonymous sessions
 * stitch to the user on the next `alias`).
 *
 * Failures are swallowed — analytics is best-effort and must never block or
 * surface on a pre-auth form.
 */
export function trackAnonEvent(event: string, properties: Record<string, unknown> = {}): void {
  void hydraUiApi.tracking
    .trackAnon({
      body: {
        anon_id: getOrCreateSignupAnonymousId(),
        event,
        properties: { ...getAnalyticsMetaProperties(), ...sanitizedLocation(), ...properties },
      },
    })
    .catch(() => {
      // Analytics is best-effort; never block or surface the funnel on a tracking failure.
    });
}
